import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EventEmitter } from 'events';
import { styled } from './styled';
import { useDebounce } from './utils/use-debounce';
import { useEmitted } from './use-emitted';
import { ZIndexContext } from './layering';
import { MouseInteraction } from './animation';
import { WebPortal } from './web-portals';
import { faListTree } from '@fortawesome/pro-light-svg-icons';
const CXMenuPositionCtx = React.createContext(null);
export function useContextMenu(options) {
    const [position, setPosition] = React.useState(null);
    const open = React.useCallback((evt) => {
        const x = evt.nativeEvent.pageX > (window.innerWidth / 2) ? { right: window.innerWidth - (evt.nativeEvent.pageX + (options?.offset?.x ?? 0)) } : { left: evt.nativeEvent.pageX + (options?.offset?.x ?? 0) };
        const y = evt.nativeEvent.pageY > (window.innerHeight / 2) ? { bottom: window.innerHeight - (evt.nativeEvent.pageY + (options?.offset?.y ?? 0)) } : { top: evt.nativeEvent.pageY + (options?.offset?.y ?? 0) };
        setPosition({
            ...x,
            ...y,
        });
    }, [options?.offset?.x, options?.offset?.y]);
    const close = React.useCallback(() => setPosition(null), []);
    const baseZIndex = React.useContext(ZIndexContext);
    const CXMenuContainer = React.useMemo(() => React.memo(function CXMenuContainer(props) {
        const ref = React.useRef(null);
        React.useEffect(() => {
            if (position !== null) {
                const listener = (clickEvent) => {
                    const path = clickEvent?.composedPath != null
                        ? event?.composedPath()
                        : // @ts-expect-error doesn't recognize path
                            clickEvent.path;
                    if (path != null && ref.current != null) {
                        const targetWasClicked = path.includes(ref.current);
                        if (!targetWasClicked) {
                            close();
                        }
                    }
                };
                window.setTimeout(() => {
                    document.addEventListener("click", listener);
                    document.addEventListener("contextmenu", listener);
                });
                return () => {
                    document.removeEventListener("click", listener);
                    document.removeEventListener("contextmenu", listener);
                };
            }
        }, [close]);
        if (position === null) {
            return null;
        }
        const Wrapper = options?.positionMode === "fixed" ? WebPortal : React.Fragment;
        return (_jsx(CXMenuPositionCtx.Provider, { value: position, children: _jsx(Wrapper, { children: _jsx("div", { style: {
                        position: options?.positionMode ?? "absolute",
                        ...position,
                        zIndex: options?.zIndex ?? (baseZIndex + 1),
                    }, ref: ref, children: props.children }) }) }));
    }), [position, close]);
    return {
        open,
        close,
        CXMenuContainer,
    };
}
export function useWrappedPopOver(options) {
    const ref = React.useRef(null);
    const containerRef = React.useRef(null);
    const [position, setPosition] = React.useState(null);
    const open = React.useCallback(() => {
        if (options?.disabled !== true) {
            const rect = ref.current?.getBoundingClientRect();
            const boundingX = rect?.left ?? 0;
            const boundingY = ref.current?.getBoundingClientRect().top ?? 0;
            const width = rect?.width ?? 0;
            const height = rect?.height ?? 0;
            const offset = typeof options?.offset === "function"
                ? options.offset({ boundingX, boundingY })
                : options?.offset ?? { x: 0, y: 0 };
            let x = "left" in offset
                ? boundingX + (offset.left ?? 0)
                : "right" in offset
                    ? boundingX + width - (offset.right ?? 0)
                    : "x" in offset
                        ? boundingX + offset.x
                        : boundingX;
            if ('xFrom' in offset && offset.xFrom === "right-edge" && rect?.width != null) {
                x += rect?.width;
            }
            const y = "top" in offset
                ? boundingY + (offset.top ?? 0)
                : "bottom" in offset
                    ? boundingY + height - (offset.bottom ?? 0)
                    : "y" in offset
                        ? boundingY + offset.y
                        : boundingY;
            const maxHeight = options?.disableMaxHeight === true ? undefined : window.innerHeight - y - 20;
            const displayViaRight = boundingX > (window.innerWidth / 2);
            setPosition(("right" in offset && options?.displayMode === "fixed") || options?.autoRLSelect === true ? {
                right: window.innerWidth - x,
                top: y,
                maxHeight,
            } : { left: x, top: y, maxHeight });
        }
    }, [options?.disabled, options?.offset]);
    const close = React.useCallback(() => {
        setPosition(null);
        options?.onClose?.();
    }, [options?.onClose]);
    React.useEffect(() => {
        if (position !== null) {
            window.requestAnimationFrame(() => {
                document.addEventListener("scroll", close);
                document.addEventListener("click", close);
            });
            return () => {
                document.removeEventListener("scroll", close);
                document.removeEventListener("click", close);
            };
        }
    }, [close, position]);
    const positionEmitter = React.useMemo(() => new EventEmitter(), []);
    const positionRef = React.useRef(position);
    const baseZIndex = React.useContext(ZIndexContext);
    React.useEffect(() => {
        positionRef.current = position;
        positionEmitter.emit("change", position);
    }, [position]);
    const CXMenuContainer = React.useMemo(() => React.memo(function CXMenuContainer(props) {
        const position = useEmitted(positionRef.current, positionEmitter);
        const Container = props.withAnimatePresence === true ? AnimatePresence : React.Fragment;
        const elem = options?.fadeAnimation === true ? (_jsx(Container, { children: _jsx(AnimatePresence, { children: position === null ? null : (_jsx(motion.div, { ref: containerRef, style: {
                        ...props.style,
                        position: options?.displayMode ?? "absolute",
                        ...position,
                        zIndex: options?.zIndex ?? (baseZIndex + 1),
                        overflowY: options?.disableMaxHeight === true ? "visible" : "hidden",
                    }, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.15 }, children: props.children })) }) })) : (_jsx(Container, { children: position === null ? null : (_jsx("div", { ref: containerRef, style: {
                    ...props.style,
                    position: options?.displayMode ?? "absolute",
                    ...position,
                    zIndex: options?.zIndex ?? (baseZIndex + 1),
                    overflowY: options?.disableMaxHeight === true ? "visible" : "hidden",
                }, children: props.children })) }));
        if (options?.displayMode === "fixed") {
            return require('react-dom').createPortal(elem, document.body);
        }
        return elem;
    }), []);
    return {
        open,
        close,
        CXMenuContainer,
        ref,
        isOpen: position !== null,
        midFoldRelative: position == null
            ? null
            : position.top > window.innerHeight * 0.5
                ? "below"
                : "above",
        midFoldRelativeY: position == null
            ? null
            : position.top > window.innerHeight * 0.5
                ? "below"
                : "above",
        midFoldRelativeX: position == null ? null : ("right" in position ? (position.right > (window.innerWidth * 0.5) ? "left" : "right") :
            "left" in position ? (position.left > window.innerWidth * 0.5 ? "right" : "left") : null),
    };
}
/**
 * Usage:
 * ```
 * const { HoverTriggerContainer, CXMenuContainer } = useHoverTriggeredWrappedPopOver({offset:{x:60, y:0}});
 *
 * <HoverTriggerContainer>
 *     <div>hover over me</div>
 *     <CXMenuContainer>
 *         this appears
 *     </CXMenuContainer>
 * </HoverTriggerContainer>
 * ```
 */
export function useHoverTriggeredWrappedPopOver(options) {
    const { open, close, isOpen, CXMenuContainer, ref, midFoldRelative, midFoldRelativeY, midFoldRelativeX } = useWrappedPopOver({
        displayMode: "fixed",
        ...options,
    });
    const delayRef = React.useRef(options?.delay ?? 300);
    React.useEffect(() => {
        delayRef.current = options?.delay ?? 300;
    }, [options?.delay]);
    const openRef = React.useRef(open);
    React.useEffect(() => {
        openRef.current = open;
    }, [open]);
    const HoverTriggerContainer = React.useMemo(() => React.memo(function HoverTriggerContainer(props) {
        const { debounce, cancel } = useDebounce(delayRef.current);
        const onMouseEnter = React.useCallback(() => {
            debounce(() => {
                openRef.current();
            });
        }, []);
        const onMouseLeave = React.useCallback(() => {
            cancel();
            close();
        }, []);
        return (_jsx("div", { ref: ref, style: props.style, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, children: props.children }));
    }), []);
    return {
        open,
        close,
        isOpen,
        CXMenuContainer,
        HoverTriggerContainer,
        midFoldRelative,
        midFoldRelativeY,
        midFoldRelativeX
    };
}
// @ts-expect-error --- override the usual styled requirement for props to start with $ cause this is a motion.div
export const ContextMenuContainerElem = styled(motion.div, ({ $theme }) => ({
    background: $theme.background1,
    display: "flex",
    flexDirection: "column",
    borderRadius: 5,
    overflow: "hidden",
    width: 200,
    gap: 1,
    boxShadow: $theme.surfaceShadow,
}));
export const ContextMenuContainer = React.memo(function ContextMenuContainer(props) {
    const position = React.useContext(CXMenuPositionCtx);
    const fromRight = position != null && "right" in position;
    const fromBottom = position != null && "bottom" in position;
    const initial = React.useRef({
        opacity: 0.5,
        scale: 0,
        x: fromRight ? "50%" : "-50%",
        y: fromBottom ? "50%" : "-50%",
    }).current;
    return (_jsx(ContextMenuContainerElem, { initial: initial, animate: {
            opacity: 1,
            scale: 1,
            x: 0,
            y: 0,
        }, transition: { duration: 100 / 1000 }, children: props.children }));
});
export const ContextMenuItemBase = styled("div", ({ $theme }) => ({
    backgroundColor: $theme.midground,
    color: $theme.foreground,
    cursor: "pointer",
    userSelect: "none",
    fontFamily: 'hiyllo',
    fontSize: 15,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
}));
const ContextMenuItemInner = styled("div", ({ $theme }) => ({
    padding: 7.5,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
}));
export const ContextMenuItem = React.memo(function ContextMenuItem(props) {
    const onClick = React.useCallback((evt) => {
        if (props.persistOnClick === true) {
            evt.stopPropagation();
        }
        if (props.onClick != null) {
            props.onClick();
        }
    }, [props]);
    return (_jsx(ContextMenuItemBase, { onClick: onClick, children: _jsx(MouseInteraction, { fullSize: true, children: _jsxs(ContextMenuItemInner, { children: [_jsx(FontAwesomeIcon, { icon: props.icon, fixedWidth: true }), props.label] }) }) }));
});
const nestedContextMenuOffsetFn = ({ boundingX }) => (boundingX > window.innerWidth / 2 ?
    {
        top: 0,
        right: 200,
    }
    :
        {
            top: 0,
            left: 200
        });
export const NestedContextMenuItem = React.memo(function NestedContextMenuItem(props) {
    const ctxMenu = useHoverTriggeredWrappedPopOver({
        displayMode: "fixed",
        offset: nestedContextMenuOffsetFn,
        zIndex: 200,
        delay: 50
    });
    return (_jsx(_Fragment, { children: _jsxs(ctxMenu.HoverTriggerContainer, { children: [_jsx(ContextMenuItem, { ...props, label: _jsxs(_Fragment, { children: [props.label, _jsx("div", { style: { flexGrow: 1 } }), _jsx(FontAwesomeIcon, { icon: faListTree })] }) }), _jsx(ctxMenu.CXMenuContainer, { children: _jsx(ContextMenuContainer, { children: props.children }) })] }) }));
});
export function useClearCtxMenus() {
    return React.useCallback(() => {
        document.dispatchEvent(new window.MouseEvent("click"));
    }, []);
}
